import { WorkshopQuotationsApi } from './workshopQuotationsPageApi';

// Formatadores
function normalizeQuotation(item = {}) {
  const tipo = String(item.tipo || item.type || 'estimativa').trim().toLowerCase();
  const valorRaw = item.valor != null ? item.valor : item.value;
  const valor = valorRaw == null || valorRaw === '' ? null : Number(valorRaw);

  return {
    id: item.id ?? item.client_quotation_id ?? null,
    tipo,
    ref_id: item.ref_id ?? item.reference_id ?? item.id ?? '',
    created_at: item.created_at ?? item.createdAt ?? null,
    valor: Number.isNaN(valor) ? null : valor,
    status: String(item.status || '').trim(),
    endereco: item.endereco ?? item.address ?? 'Oficina',
    company_id: item.company_id ?? item.companyId ?? null,
    user_id: item.user_id ?? item.userId ?? null,
    assumed_by_workshop_user_id: item.assumed_by_workshop_user_id ?? null,
    assumed_by_workshop_user_name: item.assumed_by_workshop_user_name ?? null,
    assumed_by_workshop_at: item.assumed_by_workshop_at ?? null
  };
}

function normalizeClient(cliente = {}) {
  return {
    id: cliente.id ?? cliente.company_id ?? null,
    nome: cliente.nome || cliente.company_name || '',
    email: cliente.email || '',
    telefone: cliente.telefone || cliente.phone || '',
    cnpj: cliente.cnpj || ''
  };
}

function formatQuotationsData(data) {
  const cotacoes = data.cotacoes || data.quotations || data;
  const cliente = data.cliente || data.client || {};
  
  return {
    cliente: normalizeClient(cliente),
    cotacoes: Array.isArray(cotacoes) ? cotacoes.map(normalizeQuotation) : []
  };
}

function formatarData(dataString) {
  if (!dataString) return '-';
  try {
    return new Date(dataString).toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
}

function formatarMoeda(valor) {
  if (valor == null || valor === '') return '-';
  return Number(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

function organizarCotacoesPorStatus(cotacoes) {
  const statusGrupos = {
    atencao: ["estimativa entregue", "aguardando equipamento", "pendente de aprovação", "pronto para retirada"],
    andamento: ["aguardando estimativa", "equipamento recebido", "aguardando orcamento", "aprovado para manutencao", "aguardando inspeção e laudo", "aguardando faturamento"],
    concluidos: ["recusado", "finalizado", "concluído", "estimativa recusada", "estimativa finalizada", "enviado"]
  };

  const grupos = {
    atencao: [],
    andamento: [],
    concluidos: []
  };

  cotacoes.forEach(cotacao => {
    const status = cotacao.status?.toLowerCase() || "";
    let grupo = "andamento";
    if (statusGrupos.atencao.includes(status)) grupo = "atencao";
    else if (statusGrupos.concluidos.includes(status)) grupo = "concluidos";

    grupos[grupo].push(cotacao);
  });

  return grupos;
}

// Serviços principais
function getClientQuotations(clientId) {
  return WorkshopQuotationsApi.getClientQuotations(clientId)
    .then(response => formatQuotationsData(response.data))
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao carregar cotações do cliente");
    });
}

function getClientDetails(clientId) {
  return WorkshopQuotationsApi.getClientDetails(clientId)
    .then(response => response.data)
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao carregar dados do cliente");
    });
}

function getUserQuotations() {
  return WorkshopQuotationsApi.getUserQuotations()
    .then(response => formatQuotationsData(response.data))
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao carregar cotações");
    });
}

function createQuotation(quotationData) {
  return WorkshopQuotationsApi.createQuotation(quotationData)
    .then(response => ({
      success: true,
      data: response.data,
      message: response.data.message || "Cotação criada com sucesso"
    }))
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao criar cotação");
    });
}

function getQuotationsByStatus(status) {
  return WorkshopQuotationsApi.getQuotationsByStatus(status)
    .then(response => {
      const cotacoes = response.data.cotacoes || response.data || [];
      return Array.isArray(cotacoes) ? cotacoes.map(normalizeQuotation) : [];
    })
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao filtrar cotações");
    });
}

function assumeQuotation(quotationId, user) {
  const payload = {
    quotationId: quotationId,
    userId: user.id || user.user_id,
    userName: user.name || user.nome || user.userName,
  };
  return WorkshopQuotationsApi.assumeQuotation(payload)
    .then(response => ({
      success: true,
      data: response.data
    }))
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao assumir a cotação");
    });
}

function unassignQuotation(quotationId, user) {
  const payload = {
    quotationId: quotationId,
    userId: user.id || user.user_id,
  };
  return WorkshopQuotationsApi.unassignQuotation(payload)
    .then(response => ({
      success: true,
      data: response.data
    }))
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao devolver a cotação");
    });
}

function getLinkedClients() {
  return WorkshopQuotationsApi.getLinkedClients()
    .then(response => response.data)
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao carregar empresas vinculadas");
    });
}

function linkClient(payload) {
  return WorkshopQuotationsApi.linkClient(payload)
    .then(response => response.data)
    .catch(error => {
      throw new Error(error.response?.data?.error || "Erro ao vincular nova empresa.");
    });
}

function createQuotationFromWorkshop(quotationData) {
  return WorkshopQuotationsApi.createQuotationFromWorkshop(quotationData)
    .then(response => response.data);
}

export const WorkshopQuotationsService = {
  getClientQuotations,
  getClientDetails,
  getUserQuotations,
  createQuotation,
  createQuotationFromWorkshop,
  getQuotationsByStatus,
  organizarCotacoesPorStatus,
  formatarData,
  formatarMoeda,
  assumeQuotation,
  unassignQuotation,
  getLinkedClients,
  linkClient
};
